/**
 * The Question data model and the generator contract.
 *
 * Every topic generator produces a Question. The `answer` is a structured
 * AnswerValue (never a display string) and is the source of truth for grading;
 * `prompt` carries the display forms. Worked-solution `steps` are derived from
 * the underlying math by the generator, so they can never disagree with the
 * problem. Graphing data is first-class but nullable - only graph topics use
 * it. This single shape is consumed uniformly by the practice and quiz engines.
 */

import type { AnswerValue } from "../engine/answer";
import type { TopicId, UnitId } from "../curriculum/curriculum";
import type { SubskillId } from "../curriculum/subskills";
import type { PlotModel } from "../graph/plot-model";

export type Difficulty = "easy" | "standard" | "hard" | "challenge";

export const DIFFICULTIES: readonly Difficulty[] = [
  "easy",
  "standard",
  "hard",
  "challenge",
];

/** How the student answers - drives the input UI and accepted formats. */
export type AnswerType =
  | "number" // integer or fraction
  | "decimal" // a decimal value
  | "set" // a set of solutions
  | "inequality" // single inequality
  | "compound" // compound inequality
  | "special" // no-solution / infinitely-many
  | "boolean" // yes/no
  | "choice"; // multiple choice / select the graph

/** Displayable prompt: plain text plus optional KaTeX for math typesetting. */
export interface Prompt {
  readonly text: string;
  /** KaTeX source for the math portion, when the prompt is mathematical. */
  readonly latex?: string;
}

/** One worked-solution step: a short description and the resulting state. */
export interface SolutionStep {
  readonly description: string;
  /** KaTeX for the resulting equation/expression after this step. */
  readonly latex: string;
}

/** A layered hint. Lower level = more conceptual, higher = more procedural. */
export interface Hint {
  readonly level: number;
  readonly text: string;
}

/** A selectable option for choice questions (may carry a graph to render). */
export interface Choice {
  readonly id: string;
  readonly label?: string;
  readonly latex?: string;
  readonly graph?: PlotModel;
}

/**
 * A fully-formed, validated practice/quiz question.
 *
 * `math` holds an optional topic-specific internal representation kept for
 * validation/debugging; engines never need to interpret it. `answer` is what
 * grading compares against.
 */
export interface Question {
  readonly id: string;
  readonly generator: TopicId;
  readonly generatorVersion: string;
  readonly seed: string;

  readonly unit: UnitId;
  readonly topic: TopicId;
  /** Problem-family key within the generator, for variety tracking. */
  readonly family: string;
  readonly difficulty: Difficulty;

  readonly prompt: Prompt;
  readonly answerType: AnswerType;
  readonly answer: AnswerValue;

  readonly steps: readonly SolutionStep[];
  readonly hints: readonly Hint[];
  readonly subskills: readonly SubskillId[];

  /** Present only for graphing topics. */
  readonly graph?: PlotModel;
  /** Present only for choice questions. */
  readonly choices?: readonly Choice[];

  /** Opaque internal math representation, for validation/debugging only. */
  readonly math?: unknown;
}

/**
 * A compact signature of a generated question, used by question history to
 * avoid re-presenting structurally identical problems. Not the full question.
 */
export interface QuestionSignature {
  readonly generator: TopicId;
  readonly family: string;
  readonly difficulty: Difficulty;
  /** Stable structural key (e.g. coefficient/answer fingerprint). */
  readonly structure: string;
}

/** Everything a generator needs to produce a question for one request. */
export interface GenerateContext {
  readonly seed: string;
  readonly difficulty: Difficulty;
  /**
   * Recent structural signatures to avoid duplicating. The generator may
   * regenerate (varying its internal seed derivation) to dodge collisions but
   * is not required to guarantee uniqueness.
   */
  readonly recent?: readonly QuestionSignature[];
}

/**
 * The generator contract. Implementations are pure functions of their context:
 * the same context yields the same question. A generator is responsible for
 * validating its own output before returning and must throw only on an
 * internal invariant violation, never produce an unvalidated question.
 */
export interface Generator {
  readonly topic: TopicId;
  readonly version: string;
  /** Difficulties this generator currently supports. */
  readonly supportedDifficulties: readonly Difficulty[];
  generate(ctx: GenerateContext): Question;
  /** The structural signature for a produced question (for history). */
  signature(q: Question): QuestionSignature;
}
